/** HR records (`/api/admin/v2/hr`: policies, documents, jobs, candidates) and
 *  the caller's own (`/api/admin/v2/workplace/{policies,documents,equipment}`). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import type { CandidateV2, HrDocument, HrPolicy, JobPostingV2 } from '@/client/types/hr';
import type { BizAsset } from '@/client/types/business/spend';
import { HR_BASE, WORKPLACE_BASE, hrKeys, useHrApi } from './people';

const k = (...p: unknown[]) => [...hrKeys.all, 'records', ...p] as const;
const q = { staleTime: 15_000, gcTime: DEFAULT_GC_TIME };

function useList<T>(base: string, key: readonly unknown[], path: string, field: string, enabled = true) {
  const api = useHrApi(base);
  return useQuery({ queryKey: key, queryFn: async () => ((await api<Record<string, T[]>>(path))[field] ?? []) as T[], enabled, ...q });
}

export const usePoliciesQuery = () => useList<HrPolicy>(HR_BASE, k('policies'), '/policies', 'policies');
export const usePolicyAcksQuery = (id?: string) => {
  const api = useHrApi();
  return useQuery({
    queryKey: k('acks', id),
    queryFn: () => api<{ version: number; acknowledged: { employeeId: string; at: string }[]; waiting: string[] }>(`/policies/${id}/acknowledgements`),
    enabled: Boolean(id),
    ...q,
  });
};
export const useDocumentsQuery = (employeeId?: string) =>
  useList<HrDocument>(HR_BASE, k('documents', employeeId ?? 'all'), employeeId ? `/documents?employeeId=${employeeId}` : '/documents', 'documents');
export const useJobsQuery = () => useList<JobPostingV2>(HR_BASE, k('jobs'), '/jobs', 'jobs');
export const useCandidatesQuery = (jobId?: string) =>
  useList<CandidateV2>(HR_BASE, k('candidates', jobId ?? 'all'), jobId ? `/candidates?jobId=${jobId}` : '/candidates', 'candidates');

export const useMyPoliciesQuery = () => useList<HrPolicy>(WORKPLACE_BASE, [...hrKeys.workplace(), 'policies'], '/policies', 'policies');
export const useMyDocumentsQuery = () => useList<HrDocument>(WORKPLACE_BASE, [...hrKeys.workplace(), 'documents'], '/documents', 'documents');
export const useMyEquipmentQuery = () => useList<BizAsset>(WORKPLACE_BASE, [...hrKeys.workplace(), 'equipment'], '/equipment', 'assets');

function useMut<TIn, TOut>(base: string, fn: (api: ReturnType<typeof useHrApi>, input: TIn) => Promise<TOut>) {
  const api = useHrApi(base);
  const qc = useQueryClient();
  return useMutation({ mutationFn: (input: TIn) => fn(api, input), onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }) });
}

const form = (fields: Record<string, unknown>, file?: File | null) => {
  const f = new FormData();
  Object.entries(fields).forEach(([key, v]) => v !== undefined && v !== null && f.append(key, String(v)));
  if (file) f.append('file', file);
  return f;
};

export const useUpsertPolicyMutation = () =>
  useMut(HR_BASE, (api, { id, ...body }: Partial<HrPolicy>) =>
    id ? api<HrPolicy>(`/policies/${id}`, 'PATCH', body) : api<HrPolicy>('/policies', 'POST', body),
  );
export const usePolicyActionMutation = () =>
  useMut(HR_BASE, (api, { id, action }: { id: string; action: 'publish' | 'archive' }) => api<HrPolicy>(`/policies/${id}/${action}`, 'POST'));
export const useAddDocumentMutation = () =>
  useMut(HR_BASE, (api, { employeeId, file, ...fields }: { employeeId: string; file: File; kind: string; name?: string; visibleToEmployee: boolean; expiresOn?: string }) =>
    api<HrDocument>(`/employees/${employeeId}/documents`, 'POST', form(fields, file)),
  );
export const useUpdateDocumentMutation = () =>
  useMut(HR_BASE, (api, { id, ...body }: Partial<HrDocument> & { id: string }) => api<HrDocument>(`/documents/${id}`, 'PATCH', body));
export const useDeleteDocumentMutation = () => useMut(HR_BASE, (api, id: string) => api<{ id: string }>(`/documents/${id}`, 'DELETE'));
export const useUpsertJobMutation = () =>
  useMut(HR_BASE, (api, { id, ...body }: Partial<JobPostingV2>) =>
    id ? api<JobPostingV2>(`/jobs/${id}`, 'PATCH', body) : api<JobPostingV2>('/jobs', 'POST', body),
  );
export const useUpsertCandidateMutation = () =>
  useMut(HR_BASE, (api, { id, cv, ...fields }: Partial<CandidateV2> & { cv?: File | null }) => {
    if (cv) return api<CandidateV2>(id ? `/candidates/${id}` : '/candidates', 'POST', form(fields, cv));
    return id ? api<CandidateV2>(`/candidates/${id}`, 'PATCH', fields) : api<CandidateV2>('/candidates', 'POST', fields);
  });
export const useAcknowledgePolicyMutation = () =>
  useMut(WORKPLACE_BASE, (api, id: string) => api<{ id: string }>(`/policies/${id}/acknowledge`, 'POST'));

/** Fetch a signed link and open it. */
export function useOpenSigned(base: 'hr' | 'workplace') {
  const api = useHrApi(base === 'hr' ? HR_BASE : WORKPLACE_BASE);
  return async (path: string) => {
    const { url } = await api<{ url: string }>(path);
    window.open(url, '_blank', 'noopener');
  };
}
